"use client";

import {
  useAddCarouselShows,
  useDeleteCarouselImage,
  useDeleteCarouselShow,
  useUpdateCarouselOrder,
  useUploadCarouselImage,
  useVenuePageCarousel,
} from "../../../hooks/venuePages/carousel";
import { useEffect, useRef, useState } from "react";

import { PendingUpload } from "./types";
import SelectShowsButton from "./SelectShowsButton";
import SelectShowsModal from "./SelectShowsModal";
import UploadImageButton from "./UploadImageButton";
import VenueBannerCarouselItems from "./VenueBannerCarouselItems";
import { VenueCarouselItem } from "@punchup/data/models";
import {
  FormFieldStatus,
  uploadImage,
} from "@punchup/ui-common/components/common";

interface VenueBannerCarouselSettingsCustomProps {
  venuePageId: string;
  venueSlug: string;
}

export default function VenueBannerCarouselSettingsCustom({
  venuePageId,
  venueSlug,
}: VenueBannerCarouselSettingsCustomProps) {
  const { data: carouselData, isLoading: isLoadingCarousel } =
    useVenuePageCarousel(venuePageId);
  const { mutate: uploadCarouselImage, isPending: isSavingImage } =
    useUploadCarouselImage();
  const { mutate: updateCarouselOrder, isPending: isReordering } =
    useUpdateCarouselOrder();
  const { mutate: deleteImage } = useDeleteCarouselImage();
  const { mutate: addShows, isPending: isAddingShows } = useAddCarouselShows();
  const { mutate: deleteShow } = useDeleteCarouselShow();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [optimisticItems, setOptimisticItems] = useState<
    VenueCarouselItem[] | null
  >(null);
  const [isSelectShowsOpen, setIsSelectShowsOpen] = useState(false);
  const previewUrlsRef = useRef<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);

  const isUploadingImage = pendingUploads.length > 0 || isSavingImage;

  // Remove pending uploads once they appear in server data, keeping preview URL for reuse
  useEffect(() => {
    if (!carouselData?.items || pendingUploads.length === 0) return;

    const imageItems = carouselData.items.filter(
      (item) => item.type === "image"
    );

    const serverImageIds = new Set(
      imageItems.map((item) => {
        if (item.type === "image") {
          return item.image.imageId;
        }
        return null;
      })
    );

    pendingUploads.forEach((pending) => {
      if (
        pending.cloudflareImageId &&
        serverImageIds.has(pending.cloudflareImageId)
      ) {
        previewUrlsRef.current.set(
          pending.cloudflareImageId,
          pending.previewUrl
        );
        setPendingUploads((prev) => prev.filter((p) => p.id !== pending.id));
      }
    });
  }, [carouselData?.items, pendingUploads]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setError(null);
    Array.from(files).forEach(async (file) => {
      const pendingId = crypto.randomUUID();
      const previewUrl = URL.createObjectURL(file);
      setPendingUploads((prev) => [...prev, { id: pendingId, previewUrl }]);

      try {
        const cloudflareImageId = await uploadImage(file);

        setPendingUploads((prev) =>
          prev.map((p) =>
            p.id === pendingId ? { ...p, cloudflareImageId } : p
          )
        );

        uploadCarouselImage(
          {
            venuePageId,
            data: { imageId: cloudflareImageId },
          },
          {
            onError: () => {
              setError("Failed to save image");
              URL.revokeObjectURL(previewUrl);
              setPendingUploads((prev) =>
                prev.filter((p) => p.id !== pendingId)
              );
            },
          }
        );
      } catch {
        setError("Failed to upload image");
        URL.revokeObjectURL(previewUrl);
        setPendingUploads((prev) => prev.filter((p) => p.id !== pendingId));
      }
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSelectShowsClick = () => {
    setIsSelectShowsOpen(true);
  };

  const handleAddShows = (showIds: string[]) => {
    setError(null);
    addShows(
      { venuePageId, showIds },
      {
        onSuccess: () => {
          setIsSelectShowsOpen(false);
        },
        onError: () => {
          setError("Failed to add shows");
        },
      }
    );
  };

  const handleDelete = (item: VenueCarouselItem) => {
    setError(null);
    const currentItems = optimisticItems ?? carouselData?.items ?? [];
    const filteredItems = currentItems.filter((i) => i.id !== item.id);
    setOptimisticItems(filteredItems);

    if (item.type === "image") {
      deleteImage(
        { venuePageId, imageId: item.image.id },
        {
          onError: () => {
            setError("Failed to delete image");
            setOptimisticItems(null);
          },
        }
      );
    } else if (item.type === "show") {
      deleteShow(
        { venuePageId, showId: item.show.id },
        {
          onError: () => {
            setError("Failed to delete show");
            setOptimisticItems(null);
          },
        }
      );
    } else if (item.type === "video") {
      // TODO: Implement video deletion when the API is available
      setError("Video deletion is not yet supported");
      setOptimisticItems(null);
    }
  };

  // Clear optimistic state when server data matches the expected order
  useEffect(() => {
    if (!optimisticItems || !carouselData?.items) return;

    const serverOrder = carouselData.items
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((item) => item.id);
    const optimisticOrder = optimisticItems.map((item) => item.id);

    const ordersMatch =
      serverOrder.length === optimisticOrder.length &&
      serverOrder.every((id, i) => id === optimisticOrder[i]);

    if (ordersMatch) {
      setOptimisticItems(null);
    }
  }, [carouselData?.items, optimisticItems]);

  const handleOrderChange = (reorderedItems: VenueCarouselItem[]) => {
    setError(null);
    // Update order values to reflect new positions for optimistic UI
    const itemsWithUpdatedOrder = reorderedItems.map((item, index) => ({
      ...item,
      order: index,
    }));

    // Immediately show new order (optimistic update)
    setOptimisticItems(itemsWithUpdatedOrder);

    const itemIds = reorderedItems.map((item) => item.id);
    updateCarouselOrder(
      { venuePageId, itemIds },
      {
        onError: () => {
          setError("Failed to update order");
          // Revert to server data on error
          setOptimisticItems(null);
        },
      }
    );
  };

  const getUploadedLabel = () => {
    const items = optimisticItems ?? carouselData?.items ?? [];
    if (items.length === 0) return null;

    let shows = 0;
    let images = 0;
    let videos = 0;

    for (const item of items) {
      if (item.type === "show") shows++;
      else if (item.type === "image") images++;
      else if (item.type === "video") videos++;
    }

    if (shows === 0 && images === 0 && videos === 0) return null;

    const parts: string[] = [];
    if (shows > 0)
      parts.push(`Selected: ${shows} ${shows === 1 ? "show" : "shows"}`);

    const uploadedParts: string[] = [];
    if (images > 0)
      uploadedParts.push(`${images} ${images === 1 ? "image" : "images"}`);
    if (videos > 0)
      uploadedParts.push(`${videos} ${videos === 1 ? "video" : "videos"}`);

    if (uploadedParts.length > 0)
      parts.push(`Uploaded: ${uploadedParts.join(", ")}`);

    return parts.join(", ");
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-y-1 md:gap-y-2">
          <h3 className="font-medium text-sm text-primary-blue">
            Select content type
          </h3>
          <p className="text-sm font-light text-secondary-blue">
            Choose what type of content to add to your carousel
          </p>
        </div>

        <div className="flex flex-col gap-y-5">
          <div className="flex flex-col gap-y-2 md:flex-row gap-x-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelected}
              className="hidden"
            />
            <UploadImageButton
              isUploading={isUploadingImage}
              onClick={handleUploadClick}
            />
            <SelectShowsButton
              disabled={isUploadingImage}
              onClick={handleSelectShowsClick}
            />
          </div>
          <VenueBannerCarouselItems
            isLoading={isLoadingCarousel}
            uploadedLabel={getUploadedLabel()}
            items={optimisticItems ?? carouselData?.items}
            pendingUploads={pendingUploads}
            previewUrls={previewUrlsRef.current}
            onOrderChange={handleOrderChange}
            isReordering={isReordering}
            onDelete={handleDelete}
            venuePageId={venuePageId}
          />
          <FormFieldStatus
            loading={false}
            success={false}
            error={Boolean(error)}
            errorMessage={error ?? ""}
          />
        </div>
      </div>
      {/* the modal for selecting shows */}
      <SelectShowsModal
        isOpen={isSelectShowsOpen}
        onOpenChange={setIsSelectShowsOpen}
        venuePageId={venuePageId}
        venueSlug={venueSlug}
        existingItems={optimisticItems ?? carouselData?.items ?? []}
        onAddShows={handleAddShows}
        isAdding={isAddingShows}
      />
    </>
  );
}
